import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./VideoList.scss";
import { fetchContentNotConvertAll } from "../services/apiContent";
import { fetchEpisodeStudioPending } from "../services/apiCatalogAdmin";
import { BASE_URL } from "../services/constant";

const rowsPerPage = 10;

function formatDuration(seconds) {
  if (!seconds || Number.isNaN(Number(seconds))) return "00:00";

  const safe = Number(seconds);
  const h = Math.floor(safe / 3600);
  const m = Math.floor((safe % 3600) / 60);
  const s = Math.floor(safe % 60);

  if (h === 0) {
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }

  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function buildStudioImage(path) {
  if (!path) return `${BASE_URL}/images/bg.png`;
  if (/^https?:\/\//i.test(path)) return path;
  if (path.startsWith("/uploads") || path.startsWith("/api/")) {
    return `${BASE_URL}${path}`;
  }
  return `${BASE_URL}api/${path}`;
}

function VideoList() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [currentTab, setCurrentTab] = useState("films");
  const [films, setFilms] = useState([]);
  const [episodes, setEpisodes] = useState([]);
  const [page, setPage] = useState(1);

  useEffect(() => {
    async function load() {
      try {
        const [filmData, episodeData] = await Promise.all([
          fetchContentNotConvertAll(),
          fetchEpisodeStudioPending(),
        ]);

        setFilms((filmData || []).filter((item) => item.type === "film"));
        setEpisodes(episodeData || []);
      } catch (error) {
        console.error(error);
      }
    }

    load();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [currentTab, search]);

  const activeItems = useMemo(() => {
    const source = currentTab === "films" ? films : episodes;
    const normalizedSearch = search.toLowerCase();

    return source.filter((item) => {
      const haystack = currentTab === "films"
        ? `${item.title} ${item.description || ""} ${item.title_categorie || ""}`
        : `${item.series_title || ""} ${item.season_title || ""} ${item.title || ""} ${item.description || ""}`;

      return haystack.toLowerCase().includes(normalizedSearch);
    });
  }, [currentTab, episodes, films, search]);

  const displayed = activeItems.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  return (
    <div className="page-content">
      <div className="video-list-container">
        <h2 className="page-title">Studio de conversion</h2>

        <div className="tabs">
          <button className={`tab ${currentTab === "films" ? "active" : ""}`} onClick={() => setCurrentTab("films")}>
            Films
          </button>
          <button className={`tab ${currentTab === "episodes" ? "active" : ""}`} onClick={() => setCurrentTab("episodes")}>
            Episodes series / docs
          </button>
        </div>

        <div className="filter-bar">
          <input
            type="text"
            placeholder={currentTab === "films" ? "Filtrer les films..." : "Filtrer les episodes..."}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        <table className="video-table">
          <thead>
            <tr>
              <th></th>
              <th>{currentTab === "films" ? "Film" : "Episode"}</th>
              <th>Type</th>
              <th>Categorie</th>
              <th>Date</th>
              <th>Duree</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {displayed.map((item) => {
              const isFilm = currentTab === "films";
              const thumbPath = isFilm ? item.image : item.video_img_1 || item.video_img_2 || item.image;
              return (
                <tr key={item.id}>
                  <td>
                    <input type="checkbox" />
                  </td>
                  <td className="video-cell">
                    <div className="thumb">
                      <img src={buildStudioImage(thumbPath)} alt={item.title} />
                      <span className="duration">{formatDuration(item.duration)}</span>
                    </div>
                    <div className="video-info">
                      <strong>{isFilm ? item.title : `${item.series_title || "Serie"} - ${item.title}`}</strong>
                      <p>
                        {isFilm
                          ? item.description || "Film en attente de conversion."
                          : `${item.season_title || "Saison"} • Episode ${item.episode_number}`}
                      </p>
                    </div>
                  </td>
                  <td>{isFilm ? "Film" : item.series_type === "documentaire" ? "Documentaire" : "Serie"}</td>
                  <td>{isFilm ? item.title_categorie : item.title_categorie || "-"}</td>
                  <td>{formatDate(isFilm ? item.created_at : item.created_at)}</td>
                  <td>{formatDuration(item.duration)}</td>
                  <td>
                    <button
                      type="button"
                      className="upload-choose-btn"
                      onClick={() => navigate(isFilm ? `/videoDetail/${item.id}` : `/episodeDetail/${item.id}`)}
                    >
                      Convertir
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="pagination">
          <span>
            Rows per page: <b>{rowsPerPage}</b>
          </span>
          <span>
            {page} – {Math.max(1, Math.ceil(activeItems.length / rowsPerPage))} of {activeItems.length}
          </span>
          <button disabled={page === 1} onClick={() => setPage((current) => current - 1)}>
            ‹
          </button>
          <button disabled={page * rowsPerPage >= activeItems.length} onClick={() => setPage((current) => current + 1)}>
            ›
          </button>
        </div>
      </div>
    </div>
  );
}

export default VideoList;
